/**
 * R14 Helpdesk — redesign v3 "Helpdesk" frame: Open / Resolved tabs with
 * counts, ticket cards (category icon, status pill, assignee + ETA), a
 * static "how it works" timeline card (raised → assigned → resolved — the
 * same three-stage journey as the per-ticket timeline in helpdesk/[id].tsx,
 * illustrative only, no ticket-specific data), FAB → new ticket.
 * Wired: GET /api/helpdesk?status=open|resolved.
 * Port of design/source/screens-helpdesk.jsx HelpdeskScreen.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import {
  backOr, CardSkeletons, CategoryChip, Fab, CountTabs, fmtStamp, ticketPillStatus, TimelineRow,
} from '@/components/money/bits';
import { fetchTickets, useAsync, type Ticket } from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Pill, Screen, TopBar } from '@/ui';

const JOURNEY: (readonly [DictKey, DictKey])[] = [
  ['hd_j_raised', 'hd_j_raised_sub'],
  ['hd_j_assigned', 'hd_j_assigned_sub'],
  ['hd_j_resolved', 'hd_j_resolved_sub'],
];

export default function HelpdeskScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [tab, setTab] = React.useState<'open' | 'resolved'>('open');
  const { data, loading, error, refetch } = useAsync(() => fetchTickets(tab), [tab]);

  const items = data?.items ?? [];
  const openCount = data?.openCount ?? 0;
  const resolvedCount = data?.resolvedCount ?? 0;

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('hd_title')} onBack={backOr('/(tabs)/home')} />
      <CountTabs
        tabs={[['open', t('hd_open', { n: openCount })], ['resolved', t('hd_resolved', { n: resolvedCount })]] as const}
        value={tab}
        onChange={setTab}
      />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {loading ? (
          <CardSkeletons count={3} h={120} />
        ) : error ? (
          <EmptyState
            title={t('hd_err_title')}
            body={t('hd_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon name="check-circle" size={40} color={colors.textTertiary} />}
            title={tab === 'open' ? t('hd_no_open') : t('hd_no_resolved')}
            body={tab === 'open' ? t('hd_no_open_body') : t('hd_no_resolved_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {items.map((it) => <TicketCard key={it.id} ticket={it} />)}
            <JourneyCard />
            <View style={{ height: 64 }} />
          </View>
        )}
      </Screen>
      <Fab label={t('hd_new_ticket')} onPress={() => router.push('/helpdesk/new')} />
    </View>
  );
}

function JourneyCard() {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      borderWidth: 1, borderColor: colors.borderSoft, borderRadius: 16,
      backgroundColor: colors.canvas1, padding: space.s4,
    }}>
      <AppText variant="label" tone="tertiary">{t('hd_how_moves')}</AppText>
      <View style={{ marginTop: space.s3 }}>
        {JOURNEY.map(([titleKey, subKey], i) => (
          <TimelineRow key={titleKey} title={t(titleKey)} time={t(subKey)} done={i === 0} isLast={i === JOURNEY.length - 1} />
        ))}
      </View>
    </View>
  );
}

function TicketCard({ ticket }: { ticket: Ticket }) {
  const { t } = useT();
  return (
    <Card onPress={() => router.push(`/helpdesk/${ticket.id}`)}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <CategoryChip category={ticket.category} />
        <Pill status={ticketPillStatus(ticket.status)} />
      </View>
      <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }}>{ticket.title}</AppText>
      {ticket.description ? (
        <AppText variant="bodyMd" tone="secondary" numberOfLines={2} style={{ marginTop: 4 }}>{ticket.description}</AppText>
      ) : null}
      {ticket.assignee || ticket.eta ? (
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          {[ticket.assignee, ticket.eta].filter(Boolean).join(' · ')}
        </AppText>
      ) : null}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: space.s3 }}>
        <AppText variant="bodySm" tone="tertiary" mono>#{ticket.ticketNo}</AppText>
        <AppText variant="bodySm" tone="tertiary">{t('hd_updated', { time: fmtStamp(ticket.updatedAt) })}</AppText>
      </View>
    </Card>
  );
}
